/**
 * The badge primitive. Four tones, matching `StatusPill`'s `PillKind` union so
 * the two can never drift into different vocabularies -- StatusPill IS this
 * component's main caller, and it keeps owning the `.status-pill` /
 * `data-kind` hooks the DOM suites assert on.
 */
import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export type BadgeTone = 'ok' | 'warn' | 'error' | 'neutral' | 'accent';

const TONE: Record<BadgeTone, string> = {
  ok: 'border-ok/35 bg-ok-soft text-ok',
  warn: 'border-warn/35 bg-warn-soft text-warn',
  error: 'border-danger/40 bg-danger-soft text-danger',
  neutral: 'border-line bg-surface-2 text-muted',
  accent: 'border-accent/35 bg-accent-soft text-accent',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
}

export function Badge({ className, tone = 'neutral', ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border',
        'px-2 py-0.5 text-[0.6875rem] font-medium leading-[1.4]',
        '[&_svg]:size-3 [&_svg]:shrink-0',
        TONE[tone],
        className,
      )}
      {...props}
    />
  );
}
